import type React from "react";
import { BaseComposeInput } from "@/components/common/BaseComposeInput";
import {
  POST_MAX_FILE_COUNT,
  POST_MAX_FILE_SIZE,
  POST_MAX_LENGTH,
} from "@/constants";
import type { DraftStorageError } from "@/features/composer-drafts/draftRepository";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import type { User } from "@/lib/humhub-api/types";

interface BasePostInputProps {
  placeholder?: string;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  showFormatting?: boolean;
  showAttachButton?: boolean;
  onContentChange?: (content: string) => void;
  submitDisabled?: boolean;
  hasDiscardableChanges?: boolean;
  alwaysVisible?: boolean;
  externalOnAttachClick?: () => void;
  externalFileInputRef?: React.RefObject<HTMLInputElement | null>;
  externalOnFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  className?: string;
  containerClassName?: string;
  submitIconOnly?: boolean;
  disabled?: boolean;
  maxFileSize?: number;
  maxFileCount?: number;
  allowedFileTypes?: string[];
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  hasMoreMentionUsers?: boolean;
  isFetchingMoreMentionUsers?: boolean;
  mentionLoadMoreError?: string | null;
  onLoadMoreMentionUsers?: () => Promise<User[]>;
  onMentionTrigger?: (query: string) => Promise<User[]>;
  mentionScopeKey?: string;
  compact?: boolean;
  autoFocus?: boolean;
  searchLikeWhenBlurred?: boolean;
  initialContent?: string;
  initialFiles?: File[];
  initialDraftKey?: string;
  draftScope?: ComposerDraftScope;
  onDraftRestored?: () => void;
  onDraftStorageError?: (error: DraftStorageError) => void;
  draftTargetUnavailable?: boolean;
}

export const BasePostInput: React.FC<BasePostInputProps> = ({
  placeholder,
  submitButtonLabel,
  cancelButtonLabel,
  onCancel,
  showFormatting = true,
  showAttachButton = true,
  onContentChange,
  submitDisabled = false,
  hasDiscardableChanges,
  alwaysVisible,
  externalOnAttachClick,
  externalFileInputRef,
  externalOnFileChange,
  onSubmit,
  className = "",
  containerClassName,
  submitIconOnly = false,
  disabled,
  maxFileSize = POST_MAX_FILE_SIZE,
  maxFileCount = POST_MAX_FILE_COUNT,
  allowedFileTypes,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  hasMoreMentionUsers = false,
  isFetchingMoreMentionUsers = false,
  mentionLoadMoreError,
  onLoadMoreMentionUsers,
  onMentionTrigger,
  mentionScopeKey,
  compact = false,
  autoFocus = false,
  searchLikeWhenBlurred = false,
  initialContent,
  initialFiles,
  initialDraftKey,
  draftScope,
  onDraftRestored,
  onDraftStorageError,
  draftTargetUnavailable,
}) => {
  return (
    <BaseComposeInput
      placeholder={placeholder}
      submitButtonLabel={submitButtonLabel}
      cancelButtonLabel={cancelButtonLabel}
      onCancel={onCancel}
      showFormatting={showFormatting}
      showAttachButton={showAttachButton}
      onContentChange={onContentChange}
      submitDisabled={submitDisabled}
      hasDiscardableChanges={hasDiscardableChanges}
      alwaysVisible={alwaysVisible}
      externalOnAttachClick={externalOnAttachClick}
      externalFileInputRef={externalFileInputRef}
      externalOnFileChange={externalOnFileChange}
      onSubmit={onSubmit}
      className={className}
      containerClassNameOverride={containerClassName}
      submitIconOnly={submitIconOnly}
      disabled={disabled}
      maxFileSize={maxFileSize}
      maxFileCount={maxFileCount}
      maxLength={POST_MAX_LENGTH}
      allowedFileTypes={allowedFileTypes}
      onMentionTrigger={onMentionTrigger}
      mentionScopeKey={mentionScopeKey}
      mentionUsers={mentionUsers}
      isLoadingMentionUsers={isLoadingMentionUsers}
      hasMoreMentionUsers={hasMoreMentionUsers}
      isFetchingMoreMentionUsers={isFetchingMoreMentionUsers}
      mentionLoadMoreError={mentionLoadMoreError}
      onLoadMoreMentionUsers={onLoadMoreMentionUsers}
      autoFocus={autoFocus}
      compact={compact}
      searchLikeWhenBlurred={searchLikeWhenBlurred}
      context="post"
      initialContent={initialContent}
      initialFiles={initialFiles}
      initialDraftKey={initialDraftKey}
      draftScope={draftScope}
      onDraftRestored={onDraftRestored}
      onDraftStorageError={onDraftStorageError}
      draftTargetUnavailable={draftTargetUnavailable}
    />
  );
};
